import { useProductsContextSafe } from '@/components/config-editor/providers';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useSearchProducts, useProductByPriceId } from '@/lib/api/products';
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";
import { ChevronDown, ImageOff, Package, Search, X } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PriceSelectorProps {
  value: string;
  onChange: (value: string) => void;
  storeId?: string; // Store ID for server-side search
  // Dependency injection - optional products data
  products?: any[];
  loading?: boolean;
}

/**
 * Memoized price card component
 */
const PriceCard = memo(
  ({
    price,
    variant,
    isSelected,
    onSelect,
    getPriceInfo,
    className,
  }: {
    price: any;
    variant: any;
    isSelected: boolean;
    onSelect: () => void;
    getPriceInfo: (
      p: any,
    ) => { amount: number; currency: string; recurring?: boolean; interval?: string | null } | null;
    className?: string;
  }) => {
    const priceInfo = getPriceInfo(price);
    const isRecurring = priceInfo?.recurring || price.recurring;

    return (
      <label
        htmlFor={price.id}
        className={cn(
          'flex cursor-pointer items-start gap-2 rounded border p-2 transition-all',
          'hover:border-primary/50 hover:bg-accent/50',
          'w-full',
          isSelected && 'border-primary bg-accent',
        )}
      >
        <RadioGroupItem value={price.id} id={price.id} className="mt-0.5 shrink-0" />

        {variant.imageUrl ? (
          <img
            src={variant.imageUrl}
            alt={variant.name}
            className="h-12 w-12 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded">
            <ImageOff className="text-muted-foreground h-6 w-6" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <h3 className="text-sm font-medium leading-tight">{variant.name}</h3>
            {isRecurring && (
              <span className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">Recurring</span>
            )}
          </div>

          {variant.sku && <p className="text-muted-foreground/80 mb-0.5 text-xs">{variant.sku}</p>}

          <p className="text-primary text-xs font-medium">
            {priceInfo ? formatMoney(priceInfo.amount, priceInfo.currency) : 'N/A'}
            {isRecurring && priceInfo?.interval && (
              <span className="text-muted-foreground ml-1">/ {priceInfo.interval}</span>
            )}
          </p>
        </div>
      </label>
    );
  },
);

PriceCard.displayName = 'PriceCard';

function PriceSelector({
  value,
  onChange,
  storeId,
  products: injectedProducts,
  loading: injectedLoading,
}: PriceSelectorProps) {
  const { t } = useTranslation('storefront');
  // State
  const [openProducts, setOpenProducts] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounced search update
  const updateDebouncedSearch = useDebouncedCallback((query: string) => {
    setDebouncedSearch(query);
  }, 300);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateDebouncedSearch(query);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Get products from context or injected props
  const contextData = useProductsContextSafe();
  const contextProducts = injectedProducts !== undefined ? injectedProducts : contextData?.products || [];
  const contextLoading = injectedLoading !== undefined ? injectedLoading : contextData?.loading || false;

  // Fetch product containing selected price separately (for initialization)
  const { data: selectedProductData, isLoading: isLoadingSelected } = useProductByPriceId(
    value || '',
    !!value && !!storeId && !debouncedSearch && contextProducts.length === 0,
  );

  // Use server-side search only when there's a search query and storeId
  const shouldSearch = !!storeId && !!debouncedSearch && debouncedSearch.trim().length > 0;

  const { data: searchResults, isLoading: isSearching } = useSearchProducts(
    {
      storeId: storeId || null,
      productId: null,
      searchValue: debouncedSearch || null,
    },
    { enabled: shouldSearch },
  );

  // Determine which products to use
  const allProducts = shouldSearch && searchResults ? searchResults : contextProducts;
  const loading = shouldSearch ? isSearching : contextLoading || isLoadingSelected;

  // Client-side limiting for performance (show max 100 products)
  const DISPLAY_LIMIT = 100;
  const products = useMemo(() => {
    // If there's a selected value, inject product at top if not already in list
    if (value && selectedProductData) {
      const isAlreadyInList = allProducts.some((p) =>
        p.variants?.some((v: any) => v.prices?.some((price: any) => price.id === value)),
      );

      if (!isAlreadyInList) {
        // Selected price's product is NOT in the list
        // Inject it at the top, then show others up to limit
        const others = allProducts.slice(0, DISPLAY_LIMIT - 1);
        return [selectedProductData, ...others];
      } else {
        // Selected price's product IS in the list, move it to top
        const selectedProduct = allProducts.find((p) =>
          p.variants?.some((v: any) => v.prices?.some((price: any) => price.id === value)),
        );
        const others = allProducts.filter((p) => p.id !== selectedProduct?.id).slice(0, DISPLAY_LIMIT - 1);
        return [selectedProduct, ...others];
      }
    }

    return allProducts.slice(0, DISPLAY_LIMIT);
  }, [allProducts, value, selectedProductData]);

  const hasMoreResults = allProducts.length > DISPLAY_LIMIT;

  // Count total prices for display
  const totalPrices = useMemo(() => {
    return products.reduce((sum: number, product: any) => {
      const variantPrices =
        product.variants?.reduce((vSum: number, variant: any) => {
          return vSum + (variant.prices?.length || 0);
        }, 0) || 0;
      return sum + variantPrices;
    }, 0);
  }, [products]);

  // Open the first product by default when products load
  useEffect(() => {
    if (products.length > 0) {
      setOpenProducts(new Set([products[0].id]));
    }
  }, [products.length > 0 && products[0]?.id]);

  const toggleProduct = (productId: string) => {
    setOpenProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  // Expand all products when searching and result count is small
  useEffect(() => {
    if (debouncedSearch && products.length > 0 && products.length <= 10) {
      setOpenProducts(new Set(products.map((p: any) => p.id)));
    }
  }, [debouncedSearch, products]);

  // Memoized price info getter
  const getPriceInfo = useMemo(
    () =>
      (
        price: any,
      ): { amount: number; currency: string; recurring?: boolean; interval?: string | null } | null => {
        if (price && price.currencyOptions) {
          const currencies = Object.keys(price.currencyOptions || {});
          if (currencies.length > 0) {
            const currency = currencies[0];
            const currencyData = price.currencyOptions[currency];
            if (currencyData && typeof currencyData.amount === 'number') {
              return {
                amount: currencyData.amount,
                currency: currency.toUpperCase(),
                recurring: price.recurring,
                interval: price.interval,
              };
            }
          }
        }
        return null;
      },
    [],
  );

  // Only show skeletons on INITIAL load (no data yet)
  // This prevents the "accordion effect" when refreshing with existing data
  if (loading && products.length === 0) {
    return (
      <div className="space-y-2">
        <div className="animate-pulse">
          <div className="mb-2 h-9 rounded bg-gray-200" />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse rounded-md border p-2">
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-1">
                <div className="h-4 w-48 rounded bg-gray-200" />
                <div className="h-3 w-64 rounded bg-gray-200" />
              </div>
              <div className="h-4 w-4 rounded bg-gray-200" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Search Input */}
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          type="text"
          placeholder={t('configEditor.formElements.searchProductsAndPrices')}
          value={searchQuery}
          onChange={handleSearchChange}
          className="h-9 pl-9 pr-9"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={clearSearch}
            className="text-muted-foreground hover:text-foreground absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results count */}
      {debouncedSearch && products.length > 0 && (
        <div className="text-muted-foreground text-xs">
          Found {products.length} {products.length === 1 ? 'product' : 'products'} with {totalPrices}{' '}
          {totalPrices === 1 ? 'price' : 'prices'}
        </div>
      )}

      {/* More results available notice */}
      {!debouncedSearch && !value && hasMoreResults && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 dark:border-blue-900 dark:bg-blue-950">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>
              Showing {DISPLAY_LIMIT} of {allProducts.length} products.
            </strong>{' '}
            Use search to find more prices.
          </p>
        </div>
      )}

      {/* Product List */}
      <RadioGroup value={value} onValueChange={onChange}>
        <div className="h-[400px] space-y-1 overflow-y-auto p-1.5">
          {!products || products.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8">
              <Package className="text-muted-foreground mb-2 h-8 w-8" />
              <p className="text-muted-foreground text-sm">
                {debouncedSearch ? `No prices found matching "${debouncedSearch}"` : 'No products available'}
              </p>
            </div>
          ) : (
            products.map((product) => {
              const isOpen = openProducts.has(product.id);

              // Count total prices across all variants
              const priceCount =
                product.variants?.reduce((sum: number, variant: any) => {
                  return sum + (variant.prices?.length || 0);
                }, 0) || 0;

              // Get first variant image (prefer first variant with image)
              const firstVariantImage =
                product.variants?.find((v: any) => v.imageUrl)?.imageUrl || product.variants?.[0]?.imageUrl;

              // Collect all prices from all variants
              const allPrices: Array<{ price: any; variant: any }> = [];
              product.variants?.forEach((variant: any) => {
                variant.prices?.forEach((price: any) => {
                  allPrices.push({ price, variant });
                });
              });

              return (
                <Collapsible key={product.id} open={isOpen} onOpenChange={() => toggleProduct(product.id)}>
                  <div className="rounded-md border">
                    <CollapsibleTrigger className="hover:bg-accent/50 flex w-full items-center justify-between gap-2 p-2 transition-colors">
                      {firstVariantImage ? (
                        <img
                          src={firstVariantImage}
                          alt={product.name}
                          className="h-10 w-10 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded">
                          <ImageOff className="text-muted-foreground h-5 w-5" />
                        </div>
                      )}
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-2">
                          <h2 className="text-sm font-semibold leading-tight">{product.name}</h2>
                          <span className="text-muted-foreground text-xs">
                            ({priceCount} {priceCount === 1 ? 'price' : 'prices'})
                          </span>
                        </div>
                      </div>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform duration-200',
                          isOpen && 'rotate-180',
                        )}
                      />
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2 p-2 pt-0">
                        {allPrices.length > 0 ? (
                          allPrices.map(({ price, variant }) => (
                            <PriceCard
                              key={price.id}
                              price={price}
                              variant={variant}
                              isSelected={value === price.id}
                              onSelect={() => onChange(price.id)}
                              getPriceInfo={getPriceInfo}
                            />
                          ))
                        ) : (
                          <p className="text-muted-foreground text-xs">No prices available</p>
                        )}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })
          )}
        </div>
      </RadioGroup>
    </div>
  );
}

export default PriceSelector;
